import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Checkbox,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { Nullable, floatVal } from '@jamesgmarks/utilities';
import { IQueryCommandOptions, INestedWhereCommand, IWhereCommand, TWhereOperator } from '@llws/dynamic-router-utils';

import { getMany } from '../../../redux/features/dynamic-hydra/actions';
import { useAppSelector } from '../../../redux/hooks';
import { makeYmd } from '../../../app-utils';
import { Invoice } from '../../../../../entities/hydra';
import { EFreshbooksState, EHydraState } from '../../../entity-interfaces/IInvoice';
import { roundFloatToCents } from '@hydra/internal';

// Constants
const SEARCH_DEBOUNCE_DELAY = 300;
const INVOICE_QUERY_LIMIT = 100;

interface IInvoiceSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onInvoicesSelected: (invoices: Invoice[]) => void;
  billingAccountId?: Nullable<number>;
}

export const InvoiceSelectionModal: React.FC<IInvoiceSelectionModalProps> = ({
  open,
  onClose,
  onInvoicesSelected,
  billingAccountId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Nullable<Date>>(null);
  const [endDate, setEndDate] = useState<Nullable<Date>>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());

  const invoices = useAppSelector(state => state.dynamicHydra.data.invoices?.list ?? []);

  // Helper function to create properly typed IWhereCommand objects
  const createWhereCommand = (
    field: string, value: string, operator?: TWhereOperator, notOperator?:boolean,
  ): IWhereCommand => ({
    field,
    value,
    operator,
    notOperator,
  });

  // Load invoices with search and client filter
  const loadInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const whereCommands: INestedWhereCommand[] = [
        // Invoice number search filter
        ...(searchTerm.trim()
          ? [{
            condition: 'and' as const,
            whereCommands: [
              createWhereCommand('i.invoiceNumber', `${searchTerm.trim()}`, 'like'),
            ],
          }]
          : []
        ),

        // Start date filter
        ...(startDate
          ? [{
            condition: 'and' as const,
            whereCommands: [
              createWhereCommand('i.invoiceDate', makeYmd(startDate), 'gte'),
            ],
          }]
          : []
        ),

        // End date filter
        ...(endDate
          ? [{
            condition: 'and' as const,
            whereCommands: [
              createWhereCommand('i.invoiceDate', makeYmd(endDate), 'lte'),
            ],
          }]
          : []
        ),

        // Billing account filter
        ...(billingAccountId != null && billingAccountId > 0
          ? [{
            condition: 'and' as const,
            whereCommands: [
              createWhereCommand('i.freshbooks_client_id', billingAccountId.toString()),
            ],
          }]
          : []
        ),

        // Exclude deleted invoices
        {
          condition: 'and' as const,
          whereCommands: [
            createWhereCommand('i.hydraState', 'deleted', undefined, true),
          ],
        },
      ];

      const queryOptions: IQueryCommandOptions = {
        alias: 'i',
        orderBy: [{ field: 'i.invoiceNumber', direction: 'desc' }],
        relations: [
          { path: 'i.billingAccount', alias: 'ba' },
          { path: 'ba.clients', alias: 'lc' },
        ],
        where: whereCommands,
        limit: INVOICE_QUERY_LIMIT,
      };

      getMany('invoices', queryOptions);
    } catch (err) {
      console.error('Error loading invoices:', err);
      throw new Error(`Error loading invoices: ${err}`);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, startDate, endDate, billingAccountId]);

  // Load invoices when modal opens or filters change
  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        loadInvoices();
      }, SEARCH_DEBOUNCE_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [open, loadInvoices]);

  const isInvoiceSent = useCallback((invoice: Invoice) => (
    invoice.hydraState !== EHydraState.unsent
    && invoice.freshbooksState !== EFreshbooksState.draft
  ), []);

  const isInvoicePaid = useCallback((invoice: Invoice) => {
    if (!isInvoiceSent(invoice)) {
      return false;
    }

    const outstanding = invoice.freshbooksOutstandingAmount;
    return outstanding != null && floatVal(outstanding) === 0;
  }, [isInvoiceSent]);

  // Get filtered invoices based on status filter
  const getFilteredInvoices = useCallback(() => invoices.filter(invoice => {
    if (statusFilter === 'all') return true;
    const isPaid = isInvoicePaid(invoice);
    return statusFilter === 'paid' ? isPaid : !isPaid;
  }), [invoices, statusFilter, isInvoicePaid]);

  const isInvoiceDisabled = useCallback((invoice: Invoice) => {
    return !isInvoiceSent(invoice) || isInvoicePaid(invoice) || !invoice.invoiceNumber;
  }, [isInvoicePaid, isInvoiceSent]);

  const getInvoiceStatusLabel = useCallback((invoice: Invoice) => {
    if (!isInvoiceSent(invoice)) {
      return 'Not Sent';
    }

    return isInvoicePaid(invoice) ? 'Paid' : 'Unpaid';
  }, [isInvoicePaid, isInvoiceSent]);

  const getInvoiceStatusColor = useCallback((invoice: Invoice) => {
    if (!isInvoiceSent(invoice)) {
      return 'grey.600';
    }

    return isInvoicePaid(invoice) ? 'success.main' : 'warning.main';
  }, [isInvoicePaid, isInvoiceSent]);

  const handleInvoiceToggle = useCallback((invoiceId: number) => {
    setSelectedInvoices(prev => {
      if (prev.has(invoiceId)) {
        // Remove invoice from selection - create new Set without this ID
        return new Set([...prev].filter(id => id !== invoiceId));
      } else {
        // Add invoice to selection - create new Set with this ID added
        return new Set([...prev, invoiceId]);
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const selectableInvoices = getFilteredInvoices().filter(invoice => !isInvoiceDisabled(invoice));
    const selectableIds = selectableInvoices.map(inv => inv.id);
    const allSelectableSelected = selectableInvoices.every(inv => selectedInvoices.has(inv.id));

    setSelectedInvoices(prev => {
      if (allSelectableSelected) {
        return new Set([...prev].filter(id => !selectableIds.includes(id)));
      }
      return new Set([...prev, ...selectableIds]);
    });
  }, [getFilteredInvoices, isInvoiceDisabled, selectedInvoices]);

  const handleConfirmSelection = useCallback(() => {
    const selectedInvoicesList = invoices.filter(invoice => (
      selectedInvoices.has(invoice.id) && !isInvoiceDisabled(invoice)
    ));
    onInvoicesSelected(selectedInvoicesList);
    onClose();
  }, [invoices, selectedInvoices, onInvoicesSelected, onClose, isInvoiceDisabled]);

  // Reset selected invoices when modal opens
  useEffect(() => {
    if (open) {
      setSelectedInvoices(new Set());
    }
  }, [open]);

  const formatAmount = (amount: Nullable<string>) => {
    return amount ? `$${roundFloatToCents(floatVal(amount))}` : '$0.00';
  };

  const formatDate = (date: Nullable<Date>) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  // Helper function to calculate checkbox state for select all
  const getSelectAllCheckboxState = useCallback(() => {
    const selectableInvoices = getFilteredInvoices().filter(invoice => !isInvoiceDisabled(invoice));
    const selectedCount = selectableInvoices.filter(inv => selectedInvoices.has(inv.id)).length;

    return {
      indeterminate: selectedCount > 0 && selectedCount < selectableInvoices.length,
      checked: selectableInvoices.length > 0 && selectableInvoices.every(inv => selectedInvoices.has(inv.id)),
    };
  }, [getFilteredInvoices, isInvoiceDisabled, selectedInvoices]);

  // Helper function to format selection count text
  const getSelectionCountText = useCallback(() => {
    const selectableInvoices = getFilteredInvoices().filter(invoice => !isInvoiceDisabled(invoice));
    const selectedCount = selectableInvoices.filter(inv => selectedInvoices.has(inv.id)).length;
    const plural = selectableInvoices.length !== 1 ? 's' : '';
    return `${selectedCount} of ${selectableInvoices.length} selectable invoice${plural} selected`;
  }, [getFilteredInvoices, isInvoiceDisabled, selectedInvoices]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="span">
              Select Invoices
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: '200px' }}>
                <TextField
                  label="Search by invoice number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter invoice number..."
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'paid' | 'unpaid')}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">Loading invoices...</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        {...getSelectAllCheckboxState()}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Invoice Total</TableCell>
                    <TableCell>Outstanding Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const filteredInvoices = getFilteredInvoices();

                    if (filteredInvoices.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          No invoices found
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return filteredInvoices.map((invoice) => {
                      const disabled = isInvoiceDisabled(invoice);
                      return (
                        <TableRow
                          key={invoice.id}
                          hover={!disabled}
                          sx={{
                            opacity: disabled ? 0.5 : 1,
                            backgroundColor: disabled ? 'action.hover' : 'inherit',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedInvoices.has(invoice.id)}
                              onChange={() => handleInvoiceToggle(invoice.id)}
                              disabled={disabled}
                            />
                          </TableCell>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell>
                            {invoice.billingAccount?.clients?.[0]?.clientName ?? 'Unknown Client'}
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>{
                            formatAmount(
                              (floatVal(invoice.amountInvoiced) + floatVal(invoice.taxes)).toString(),
                            )
                          }</TableCell>
                          <TableCell>{formatAmount(invoice.freshbooksOutstandingAmount)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: getInvoiceStatusColor(invoice),
                                color: 'white',
                              }}
                            >
                              {getInvoiceStatusLabel(invoice)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {getSelectionCountText()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmSelection}
              disabled={!invoices.some(invoice => selectedInvoices.has(invoice.id) && !isInvoiceDisabled(invoice))}
            >
            Add Selected Invoices
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default InvoiceSelectionModal;
